import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { PublicLayout } from "@/components/Navbar";
import { CheckCircle2, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";

export default function Checkout() {
  const { cart, refreshCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [po, setPo] = useState("");
  const [notes, setNotes] = useState("");
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);
  const idempotencyKey = useMemo(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())), []);

  useEffect(() => { refreshCart(); /* eslint-disable-next-line */ }, []);

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/retailer/checkout", { idempotency_key: idempotencyKey, po_reference: po, delivery_notes: notes });
      setOrder(data);
      await refreshCart();
      toast.success(`Order ${data.order_number} placed`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not place order");
    } finally { setPlacing(false); }
  };

  if (order) {
    const syncColor = order.sage_status === "SYNCED" ? "text-emerald-400" : order.sage_status === "REQUIRES ATTENTION" ? "text-rose-400" : "text-amber-400";
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-400" />
          <h1 className="mt-6 font-serif-display text-4xl font-bold text-slate-50">Order Confirmed</h1>
          <p className="mt-2 text-sm text-[#94A3B8]">Stock has been reserved immediately against Sage 50.</p>

          <div className="mt-8 rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6 text-left">
            <div className="flex justify-between border-b border-[#2A2E39] pb-4">
              <div><div className="text-xs text-[#64748B]">M&C Order</div><div data-testid="order-number" className="font-mono-data text-lg font-bold text-[#D4AF37]">{order.order_number}</div></div>
              <div className="text-right"><div className="text-xs text-[#64748B]">Sage Status</div><div data-testid="order-sage-status" className={`font-semibold ${syncColor}`}>{order.sage_status}</div></div>
            </div>
            {order.sage_order_number && <div className="flex justify-between pt-4 text-sm"><span className="text-[#94A3B8]">Sage Sales Order</span><span className="font-mono-data text-slate-100">#{order.sage_order_number}</span></div>}
            <div className="mt-2 space-y-2">
              {order.items.map((it) => (
                <div key={it.sku} className="flex justify-between text-sm"><span className="text-slate-200">{it.quantity} × {it.name}</span><span className="text-slate-100">€{it.line_total.toFixed(2)}</span></div>
              ))}
            </div>
            <div className="mt-4 flex justify-between border-t border-[#2A2E39] pt-4 font-bold"><span className="text-slate-100">Total (incl. VAT)</span><span className="text-[#D4AF37]">€{order.total.toFixed(2)}</span></div>
          </div>

          <div className="mt-8 flex justify-center gap-3">
            <Link to="/portal/orders" data-testid="view-orders-btn" className="mc-btn-gold rounded-full px-7 py-3">View My Orders</Link>
            <Link to="/portal" className="rounded-full border border-[#2A2E39] px-7 py-3 font-semibold text-slate-200 hover:border-[#D4AF37]/50">Continue Ordering</Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-8">
        <h1 className="font-serif-display text-4xl font-bold text-slate-50">Checkout</h1>

        {cart.items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-[#2A2E39] p-12 text-center text-[#64748B]">
            Your cart is empty. <Link to="/portal" className="text-[#D4AF37]">Browse products</Link>
          </div>
        ) : (
          <div className="mt-10 grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
                <div className="text-sm font-semibold text-slate-100">Delivery to</div>
                <div className="mt-1 text-sm text-[#94A3B8]">{user?.company_name} — {user?.name}</div>
              </div>
              <div className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
                <label className="mb-1.5 block text-sm font-medium text-slate-200">Purchase Order Reference (optional)</label>
                <input data-testid="checkout-po" value={po} onChange={(e) => setPo(e.target.value)} className="w-full rounded-xl border border-[#2A2E39] bg-[#0B0C0E] px-4 py-3 text-sm text-slate-100 focus:border-[#D4AF37]/60 focus:outline-none" />
                <label className="mb-1.5 mt-4 block text-sm font-medium text-slate-200">Delivery Notes (optional)</label>
                <textarea data-testid="checkout-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-xl border border-[#2A2E39] bg-[#0B0C0E] px-4 py-3 text-sm text-slate-100 focus:border-[#D4AF37]/60 focus:outline-none" />
              </div>
              <div className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
                <div className="space-y-2">
                  {cart.items.map((it) => (
                    <div key={it.sku} className="flex justify-between text-sm"><span className="text-slate-200">{it.quantity} × {it.name}</span><span className="text-slate-100">€{it.line_total.toFixed(2)}</span></div>
                  ))}
                </div>
              </div>
            </div>

            <div className="h-fit rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
              <h3 className="text-lg font-semibold text-slate-100">Summary</h3>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-[#94A3B8]"><dt>Subtotal</dt><dd className="text-slate-100">€{cart.subtotal.toFixed(2)}</dd></div>
                <div className="flex justify-between text-[#94A3B8]"><dt>VAT</dt><dd className="text-slate-100">€{cart.vat_total.toFixed(2)}</dd></div>
                <div className="mt-2 flex justify-between border-t border-[#2A2E39] pt-3 text-base font-bold"><dt className="text-slate-100">Total</dt><dd className="text-[#D4AF37]">€{cart.total.toFixed(2)}</dd></div>
              </dl>
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3 text-xs text-[#B6BECD]">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
                Stock is validated live with Sage 50 and reserved the instant you place this order.
              </div>
              <button data-testid="place-order-btn" disabled={placing} onClick={placeOrder} className="mc-btn-gold mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 disabled:opacity-60">
                {placing ? <><Loader2 className="h-4 w-4 animate-spin" /> Validating stock…</> : "Place Order"}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[11px] text-[#64748B]"><AlertTriangle className="h-3 w-3" /> Order on account — no payment required.</p>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
